<template>
  <div v-on-clickaway="onClose" class="bulk-action__status">
    <div class="triangle">
      <svg height="12" viewBox="0 0 24 12" width="24">
        <path d="M20 12l-8-8-12 12" fill-rule="evenodd" stroke-width="1px" />
      </svg>
    </div>
    <div class="header flex-between">
      <span>{{ $t('BULK_ACTION.CONTACT_STATUS.TITLE') }}</span>
      <woot-button
        size="tiny"
        variant="clear"
        color-scheme="secondary"
        icon="dismiss"
        @click="onClose"
      />
    </div>
    <div class="container">
      <div class="status__list-container">
        <!-- Board Selection -->
        <div class="section-header">
          <span>{{ $t('BULK_ACTION.CONTACT_STATUS.SELECT_BOARD') }}</span>
        </div>
        <ul>
          <li class="search-container">
            <div class="status-list-search flex-between">
              <fluent-icon icon="search" class="search-icon" size="16" />
              <input
                v-model="boardQuery"
                type="search"
                placeholder="Search boards..."
                class="status--search_input"
              />
            </div>
          </li>
          <template v-if="filteredBoards.length">
            <li v-for="board in filteredBoards" :key="board.id">
              <div
                class="status__list-item"
                :class="{ 'selected-item': selectedBoard && selectedBoard.id === board.id }"
                @click="selectBoard(board)"
              >
                <fluent-icon
                  v-if="selectedBoard && selectedBoard.id === board.id"
                  icon="checkmark"
                  size="14"
                  class="mr-2"
                />
                <span class="status-option__title">{{ board.name }}</span>
              </div>
            </li>
          </template>
          <li v-else>
            <div class="status__list-item no-results">
              <span>{{ $t('BULK_ACTION.CONTACT_STATUS.NO_BOARDS') }}</span>
            </div>
          </li>
        </ul>

        <!-- Pipeline/Status Selection (only show if board selected with valid id and has statuses) -->
        <div v-if="selectedBoard && selectedBoard.id && boardHasStatuses" class="pipeline-section">
          <div class="section-header">
            <span>{{ $t('BULK_ACTION.CONTACT_STATUS.SELECT_PIPELINE') }}</span>
          </div>
          <ul>
            <li class="search-container">
              <div class="status-list-search flex-between">
                <fluent-icon icon="search" class="search-icon" size="16" />
                <input
                  v-model="statusQuery"
                  type="search"
                  placeholder="Search pipeline..."
                  class="status--search_input"
                />
              </div>
            </li>
            <template v-if="filteredStatuses.length">
              <li v-for="status in filteredStatuses" :key="status.id">
                <div
                  class="status__list-item"
                  :class="{ 'selected-item': selectedStatus && selectedStatus.id === status.id }"
                  @click="selectStatus(status)"
                >
                  <fluent-icon
                    v-if="selectedStatus && selectedStatus.id === status.id"
                    icon="checkmark"
                    size="14"
                    class="mr-2"
                  />
                  <span class="status-option__title">{{ status.name }}</span>
                </div>
              </li>
            </template>
            <li v-else>
              <div class="status__list-item no-results">
                <span>{{ $t('BULK_ACTION.CONTACT_STATUS.NO_PIPELINES') }}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <!-- Apply Button - outside scrollable container -->
    <div class="actions-container">
      <woot-button
        size="small"
        is-expanded
        color-scheme="primary"
        :disabled="!hasSelection"
        @click="applyChanges"
      >
        {{ $t('BULK_ACTION.CONTACT_STATUS.APPLY_CHANGES') }}
      </woot-button>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { mapGetters } from 'vuex';

export default {
  mixins: [clickaway],
  data() {
    return {
      boardQuery: '',
      statusQuery: '',
      selectedBoard: null,
      selectedStatus: null,
    };
  },
  computed: {
    ...mapGetters({
      boards: 'pipelines/getAllBoards',
    }),
    filteredBoards() {
      const boardList = [{ id: null, name: 'None' }, ...(this.boards || [])];
      if (this.boardQuery) {
        return boardList.filter(board =>
          board.name.toLowerCase().includes(this.boardQuery.toLowerCase())
        );
      }
      return boardList;
    },
    // Get statuses from selected board (API returns statuses_attributes)
    boardStatuses() {
      if (!this.selectedBoard || !this.selectedBoard.id) return [];
      return this.selectedBoard.statuses_attributes || this.selectedBoard.statuses || [];
    },
    // Check if board has any statuses
    boardHasStatuses() {
      return this.boardStatuses.length > 0;
    },
    filteredStatuses() {
      if (!this.boardHasStatuses) return [];
      // No "None" option for pipeline - if board is selected, pipeline must be selected
      const statusList = [...this.boardStatuses];
      if (this.statusQuery) {
        return statusList.filter(status =>
          status.name.toLowerCase().includes(this.statusQuery.toLowerCase())
        );
      }
      return statusList;
    },
    hasSelection() {
      // If board is "None" (id is null), allow selection
      // If board is selected (has id), require pipeline to be selected too
      if (this.selectedBoard === null) return false;
      if (this.selectedBoard.id === null) return true; // "None" board selected
      // Board with id selected - require pipeline selection if board has statuses
      if (this.boardHasStatuses) {
        return this.selectedStatus !== null && this.selectedStatus.id !== null;
      }
      return true; // Board selected but has no statuses
    },
  },
  mounted() {
    this.$store.dispatch('pipelines/getBoards');
  },
  methods: {
    selectBoard(board) {
      this.selectedBoard = board;
      this.selectedStatus = null; // Reset status when board changes
      this.statusQuery = '';
      // Scroll to pipeline section when board with statuses is selected
      if (board && board.id) {
        this.$nextTick(() => {
          const pipelineSection = this.$el.querySelector('.pipeline-section');
          if (pipelineSection) {
            pipelineSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    selectStatus(status) {
      this.selectedStatus = status;
    },
    applyChanges() {
      const statusData = {};
      if (this.selectedBoard) {
        statusData.board_id = this.selectedBoard.id;
        // If "None" board is selected (id is null), also set status_id to null
        if (this.selectedBoard.id === null) {
          statusData.status_id = null;
        } else if (this.selectedStatus) {
          statusData.status_id = this.selectedStatus.id;
        }
      }
      this.$emit('update-status', statusData);
    },
    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.bulk-action__status {
  @apply w-[15rem] absolute right-2 top-12 origin-top-right z-20 max-w-[18rem] bg-white dark:bg-slate-800 rounded-lg border border-solid border-slate-50 dark:border-slate-700 shadow-md flex flex-col;
  
  .header {
    @apply p-2.5 border-b border-solid border-slate-100 dark:border-slate-700 flex-shrink-0;

    span {
      @apply text-sm font-medium;
    }
  }

  .container {
    @apply overflow-y-auto max-h-[16rem] flex-1;
    
    .status__list-container {
      @apply h-full;
    }

    .section-header {
      @apply py-2 px-2.5 bg-slate-50 dark:bg-slate-900 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide;
    }

    .status-list-search {
      @apply py-0 px-2.5 bg-slate-50 dark:bg-slate-900 border border-solid border-slate-100 dark:border-slate-600/70 rounded-md;
      
      .search-icon {
        @apply text-slate-400 dark:text-slate-200;
      }

      .status--search_input {
        @apply border-0 text-xs m-0 dark:bg-transparent bg-transparent h-[unset];
      }
    }
  }
  
  .triangle {
    right: var(--triangle-position);
    @apply block z-10 absolute text-left -top-3;

    svg path {
      @apply fill-white dark:fill-slate-800 stroke-slate-50 dark:stroke-slate-600/50;
    }
  }
}

ul {
  @apply m-0 list-none;

  li {
    &:last-child {
      .status__list-item {
        @apply last:rounded-b-lg;
      }
    }
  }
}

.status__list-item {
  @apply flex items-center p-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900;
  
  &.selected-item {
    @apply bg-woot-50 dark:bg-woot-800/20;
  }
  
  &.no-results {
    @apply cursor-default text-slate-400;
  }
  
  span {
    @apply text-sm;
  }
}

.search-container {
  @apply py-1 px-2.5 sticky top-0 z-20 bg-white dark:bg-slate-800;
}

.actions-container {
  @apply p-2.5 border-t border-solid border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-b-lg flex-shrink-0;
}
/* Center expanded woot-button inside actions-container */
:deep(.actions-container) button.button.expanded {
  display: flex;
  justify-content: center;
}

/* Center the text inside */
:deep(.actions-container) .button__content {
  width: 100%;
  text-align: center;
}
</style>
